import { Appointment, AppointmentsResponse } from './appointments.models';

export interface AppointmentsState {
  ids: string[];
  entities: Record<string, Appointment>;
  selectedId: string | null;
  error: string | null;
  isLoaded: boolean;
  isLoading: boolean;
}

type Action =
  | { type: 'FETCH_APPOINTMENTS' }
  | {
      type: 'FETCH_APPOINTMENTS_SUCCESS';
      payload: { appointments: AppointmentsResponse[] };
    }
  | { type: 'FETCH_APPOINTMENTS_FAILURE'; payload: { error: string } }
  | { type: 'FETCH_APPOINTMENTS_CANCELLED' }
  | { type: 'FETCH_APPOINTMENTS_SKIPPED' }
  | { type: 'FETCH_APPOINTMENT'; payload: { appointmentId: string } }
  | { type: 'FETCH_APPOINTMENT_SUCCESS'; payload: { appointment: Appointment } }
  | { type: 'FETCH_APPOINTMENT_FAILURE'; payload: { error: string } }
  | { type: 'FETCH_APPOINTMENT_CANCELLED' }
  | { type: 'SET_SELECTED_APPOINTMENT_ID'; payload: { appointmentId: string } }
  | { type: 'UPSERT_APPOINTMENTS'; payload: { appointments: Appointment[] } }
  | { type: 'REMOVE_APPOINTMENTS'; payload: { appointmentIds: string[] } };

export const initState: AppointmentsState = {
  ids: [],
  entities: {},
  selectedId: null,
  error: null,
  isLoaded: false,
  isLoading: true,
};

export const appointmentsReducer = (
  state: AppointmentsState,
  action: Action
): AppointmentsState => {
  const preserveDetailsData = (
    curr: AppointmentsResponse,
    app?: Appointment
  ): Appointment => ({
    ...curr,
    job: {
      ...curr.job,
      tagTypes: app?.job.tagTypes || [],
      assets: app?.job.assets || [],
      callSummaries: app?.job.callSummaries || [],
    },
    customer: {
      ...curr.customer,
      contacts: app?.customer.contacts || [],
    },
    memberships: app?.memberships || [],
    activities: app?.activities || [],
  });

  switch (action.type) {
    case 'FETCH_APPOINTMENTS':
      return {
        ...state,
        error: null,
        isLoading: true,
        isLoaded: !!state.ids.length,
      };

    case 'FETCH_APPOINTMENTS_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointments.map((a) => a.appointmentId),
          ]),
        ],
        entities: action.payload.appointments.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.appointmentId]: preserveDetailsData(
              curr,
              prev[curr.appointmentId]
            ),
          }),
          state.entities
        ),
      };

    case 'SET_SELECTED_APPOINTMENT_ID':
    case 'FETCH_APPOINTMENT':
      return {
        ...state,
        error: null,
        isLoading: action.type === 'FETCH_APPOINTMENT',
        isLoaded: state.ids.includes(action.payload.appointmentId),
        selectedId: action.payload.appointmentId,
      };

    case 'FETCH_APPOINTMENT_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
        ids: [
          ...new Set([...state.ids, action.payload.appointment.appointmentId]),
        ],
        entities: {
          ...state.entities,
          [action.payload.appointment.appointmentId]:
            action.payload.appointment,
        },
      };

    case 'FETCH_APPOINTMENTS_CANCELLED':
    case 'FETCH_APPOINTMENT_CANCELLED':
      return {
        ...state,
        error: null,
      };

    case 'FETCH_APPOINTMENTS_SKIPPED':
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };

    case 'FETCH_APPOINTMENTS_FAILURE':
    case 'FETCH_APPOINTMENT_FAILURE':
      return {
        ...state,
        isLoaded: true,
        isLoading: false,
        error: action.payload.error,
      };

    case 'UPSERT_APPOINTMENTS':
      return {
        ...state,
        isLoading: state.isLoading,
        isLoaded: true,
        ids: [
          ...new Set([
            ...state.ids,
            ...action.payload.appointments.map((a) => a.appointmentId),
          ]),
        ],
        entities: action.payload.appointments.reduce(
          (prev, curr) => ({ ...prev, [curr.appointmentId]: curr }),
          state.entities
        ),
      };

    case 'REMOVE_APPOINTMENTS':
      return {
        ...state,
        ids: state.ids.filter(
          (id) => !action.payload.appointmentIds.includes(id)
        ),
      };

    default:
      return state;
  }
};
